import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTenantContext } from '../../context/TenantContext';
import { useInstructorAuthContext } from '../../context/InstructorAuthContext';
import { useToast } from '../../hooks/useToast';
import { InstructorLayout } from '../../components/instructor/InstructorLayout';
import { 
  User, 
  Mail, 
  Phone, 
  Globe, 
  MapPin,
  Calendar,
  Edit,
  Save,
  X,
  Upload,
  Camera,
  Link,
  Award,
  BookOpen,
  Users,
  Star,
  Plus,
  Trash
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Avatar, AvatarImage, AvatarFallback } from '../../components/ui/avatar';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Separator } from '../../components/ui/separator';

interface InstructorProfile {
  id: string;
  name: string;
  email: string;
  phone: string | null;
  bio: string | null;
  avatar_url: string | null;
  website: string | null;
  location: string | null;
  expertise: string[];
  social_links: {
    linkedin?: string;
    twitter?: string;
    github?: string;
    youtube?: string;
  };
  education: Education[];
  certifications: Certification[];
  experience: Experience[];
  created_at: string;
  stats: {
    totalCourses: number;
    totalStudents: number;
    averageRating: number;
    totalReviews: number;
  };
}

interface Education {
  id: string;
  institution: string;
  degree: string;
  field: string;
  startYear: number;
  endYear: number | null;
  description?: string;
}

interface Certification {
  id: string;
  name: string;
  issuer: string;
  issueDate: string;
  expiryDate?: string;
  credentialId?: string;
  credentialUrl?: string;
}

interface Experience {
  id: string;
  company: string;
  position: string;
  startDate: string;
  endDate: string | null;
  description: string;
  current: boolean;
}

const InstructorProfilePage: React.FC = () => {
  const { currentTenant } = useTenantContext();
  const { instructor: authInstructor, refreshProfile, updateProfile } = useInstructorAuthContext();
  const { showToast } = useToast();
  const navigate = useNavigate();
  
  const [profile, setProfile] = useState<InstructorProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [editedProfile, setEditedProfile] = useState<Partial<InstructorProfile>>({});
  const [newExpertise, setNewExpertise] = useState('');

  // 1) Rafraîchir une fois au montage
  useEffect(() => {
    (async () => {
      await refreshProfile();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 2) Quand le profil auth est mis à jour, recharger l'état local
  useEffect(() => {
    if (authInstructor) {
      loadProfile();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authInstructor]);

  const loadProfile = async () => {
    try {
      setLoading(true);
      
      // Utiliser les données de l'instructeur du contexte d'authentification
      if (authInstructor) {
        setProfile({
          id: authInstructor.id,
          name: authInstructor.name,
          email: authInstructor.email,
          phone: authInstructor.phone || null,
          bio: authInstructor.bio || null,
          avatar_url: authInstructor.avatar_url || null,
          website: authInstructor.website || null,
          location: authInstructor.location || null,
          expertise: authInstructor.expertise || [],
          social_links: authInstructor.social_links || {},
          education: authInstructor.education || [],
          certifications: authInstructor.certifications || [],
          experience: authInstructor.experience || [],
          created_at: authInstructor.created_at || new Date().toISOString(),
          stats: {
            totalCourses: authInstructor.totalCourses || 0,
            totalStudents: authInstructor.totalStudents || 0,
            averageRating: authInstructor.averageRating || 0,
            totalReviews: authInstructor.totalReviews || 0
          }
        });
      }
    } catch (error) {
      console.error('Erreur lors du chargement du profil:', error);
      showToast('Erreur lors du chargement du profil', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = () => {
    setEditing(true);
    setEditedProfile({ ...profile });
  };

  const handleCancel = () => {
    setEditing(false);
    setEditedProfile({});
  };

  const handleSave = async () => {
    try {
      if (!profile) return;
      const payload = {
        name: editedProfile.name ?? profile.name,
        email: editedProfile.email ?? profile.email,
        phone: editedProfile.phone ?? profile.phone ?? null,
        website: editedProfile.website ?? profile.website ?? null,
        bio: editedProfile.bio ?? profile.bio ?? null,
        avatar_url: editedProfile.avatar_url ?? profile.avatar_url ?? null,
      };

      const ok = await updateProfile(payload as any);
      if (!ok) {
        showToast("Échec de la mise à jour du profil", 'error');
        return;
      }

      await refreshProfile();
      setProfile(prev => prev ? { ...prev, ...payload } as InstructorProfile : prev);
      setEditing(false);
      setEditedProfile({});
      showToast('Profil mis à jour avec succès', 'success');
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
      showToast('Erreur lors de la sauvegarde du profil', 'error');
    }
  };

  const handleAddExpertise = () => {
    if (newExpertise.trim() && editedProfile.expertise) {
      setEditedProfile({
        ...editedProfile,
        expertise: [...editedProfile.expertise, newExpertise.trim()]
      });
      setNewExpertise('');
    }
  };

  const handleRemoveExpertise = (index: number) => {
    if (editedProfile.expertise) {
      setEditedProfile({
        ...editedProfile,
        expertise: editedProfile.expertise.filter((_, i) => i !== index)
      });
    }
  };

  const formatDate = (dateString: string) => {
    return new Intl.DateTimeFormat('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    }).format(new Date(dateString));
  };

  if (loading) {
    return (
      <InstructorLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </InstructorLayout>
    );
  }

  if (!profile && !authInstructor) {
    return (
      <InstructorLayout>
        <div className="p-6">
          <div className="text-center py-12">
            <User className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">Profil non disponible</h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Impossible de charger les informations du profil. Veuillez vous reconnecter.
            </p>
            <div className="mt-6">
              <Button onClick={() => navigate('/instructor/login')}>Se reconnecter</Button>
            </div>
          </div>
        </div>
      </InstructorLayout>
    );
  }

  if (!profile) {
    return (
      <InstructorLayout>
        <div className="p-6">
          <div className="text-center py-12">
            <User className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">Aucun profil trouvé</h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Impossible de charger les informations du profil.
            </p>
          </div>
        </div>
      </InstructorLayout>
    );
  }

  return (
    <InstructorLayout>
      <div className="space-y-6">
        {/* En-tête */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-white">Mon profil</h1>
            <p className="text-gray-400 mt-1">
              Gérez vos informations personnelles et professionnelles
            </p>
          </div>
          <div className="flex items-center gap-2">
            {editing ? (
              <>
                <Button variant="outline" onClick={handleCancel} className="border border-gray-700 text-gray-300 hover:bg-gray-800/40">
                  <X className="h-4 w-4 mr-2" />
                  Annuler
                </Button>
                <Button onClick={handleSave} className="bg-gradient-to-r from-blue-600 to-indigo-700 hover:from-blue-700 hover:to-indigo-800 text-white shadow-lg">
                  <Save className="h-4 w-4 mr-2" />
                  Sauvegarder
                </Button>
              </>
            ) : (
              <Button onClick={handleEdit} className="bg-gradient-to-r from-blue-600 to-indigo-700 hover:from-blue-700 hover:to-indigo-800 text-white shadow-lg">
                <Edit className="h-4 w-4 mr-2" />
                Modifier
              </Button>
            )}
          </div>
        </div>

        <div className="space-y-6">
            {/* Informations de base */}
            <Card className="bg-gradient-to-br from-gray-800/50 to-gray-900/50 border-gray-700/30 shadow-lg hover:shadow-xl hover:ring-1 hover:ring-indigo-500/30 transition-all">
              <CardHeader>
                <CardTitle className="text-white">Informations personnelles</CardTitle>
                <CardDescription className="text-gray-400">Ces informations sont visibles dans vos cours et communications</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="flex items-start gap-6">
                  <div className="relative">
                    <Avatar className="h-24 w-24 ring-1 ring-gray-700/60">
                      <AvatarImage src={profile.avatar_url || undefined} />
                      <AvatarFallback className="text-lg bg-gradient-to-br from-blue-900 to-indigo-900 text-blue-200">
                        {profile.name.split(' ').map(n => n[0]).join('')}
                      </AvatarFallback>
                    </Avatar>
                    {editing && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="absolute -bottom-2 -right-2 h-8 w-8 p-0 border border-gray-700 text-gray-300 hover:bg-gray-800/40"
                      >
                        <Camera className="h-3 w-3" />
                      </Button>
                    )}
                  </div>
                  
                  <div className="flex-1 space-y-5">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-start">
                      <div>
                        <Label htmlFor="name" className="text-sm text-gray-400 flex items-center gap-2"><User className="h-4 w-4" /> Nom complet</Label>
                        {editing ? (
                          <Input
                            id="name"
                            value={editedProfile.name || profile.name}
                            onChange={(e) => setEditedProfile({ ...editedProfile, name: e.target.value })}
                            className="bg-gray-900/40 text-white placeholder-gray-400 border border-gray-700 focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:border-blue-500"
                          />
                        ) : (
                          <p className="mt-1 text-[15px] leading-6 text-gray-100">{profile.name}</p>
                        )}
                      </div>
                      
                      <div>
                        <Label htmlFor="email" className="text-sm text-gray-400 flex items-center gap-2"><Mail className="h-4 w-4" /> Email</Label>
                        {editing ? (
                          <Input
                            id="email"
                            type="email"
                            value={editedProfile.email || profile.email}
                            onChange={(e) => setEditedProfile({ ...editedProfile, email: e.target.value })}
                            className="bg-gray-900/40 text-white placeholder-gray-400 border border-gray-700 focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:border-blue-500"
                          />
                        ) : (
                          <p className="mt-1 text-[15px] leading-6 text-gray-100 break-words">{profile.email}</p>
                        )}
                      </div>
                      
                      <div>
                        <Label htmlFor="phone" className="text-sm text-gray-400 flex items-center gap-2"><Phone className="h-4 w-4" /> Téléphone</Label>
                        {editing ? (
                          <Input
                            id="phone"
                            value={editedProfile.phone || profile.phone || ''}
                            onChange={(e) => setEditedProfile({ ...editedProfile, phone: e.target.value })}
                            className="bg-gray-900/40 text-white placeholder-gray-400 border border-gray-700 focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:border-blue-500"
                          />
                        ) : (
                          <p className="mt-1 text-[15px] leading-6 text-gray-100">{profile.phone || 'Non renseigné'}</p>
                        )}
                      </div>
                      <div>
                        <Label htmlFor="website" className="text-sm text-gray-400 flex items-center gap-2"><Globe className="h-4 w-4" /> Site web</Label>
                        {editing ? (
                          <Input
                            id="website"
                            value={editedProfile.website || profile.website || ''}
                            onChange={(e) => setEditedProfile({ ...editedProfile, website: e.target.value })}
                            className="bg-gray-900/40 text-white placeholder-gray-400 border border-gray-700 focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:border-blue-500"
                          />
                        ) : (
                          <p className="mt-1 text-[15px] leading-6 text-gray-100 break-words">
                            {profile.website ? (
                              <a href={profile.website} target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:text-blue-300 hover:underline break-words">
                                {profile.website}
                              </a>
                            ) : (
                              'Non renseigné'
                            )}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                <Separator className="my-2 bg-gray-700/40" />
                <div>
                  <Label htmlFor="bio" className="text-gray-400">Biographie</Label>
                  {editing ? (
                    <Textarea
                      id="bio"
                      rows={4}
                      value={editedProfile.bio || profile.bio || ''}
                      onChange={(e) => setEditedProfile({ ...editedProfile, bio: e.target.value })}
                      placeholder="Parlez-nous de vous, votre expérience et votre passion pour l'enseignement..."
                      className="bg-gray-900/40 text-white placeholder-gray-400 border border-gray-700 focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:border-blue-500"
                    />
                  ) : (
                    <p className="mt-1 text-sm text-gray-200 whitespace-pre-wrap">
                      {profile.bio || 'Aucune biographie renseignée'}
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Membre depuis */}
            <Card className="bg-gradient-to-br from-gray-800/50 to-gray-900/50 border-gray-700/30 shadow-lg hover:shadow-xl hover:ring-1 hover:ring-indigo-500/30 transition-all">
              <CardHeader>
                <CardTitle className="text-white">Informations du compte</CardTitle>
                <CardDescription className="text-gray-400">Détails de votre inscription</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-3">
                  <Calendar className="h-4 w-4 text-gray-400" />
                  <div>
                    <p className="text-sm font-medium text-white">Membre depuis</p>
                    <p className="text-sm text-gray-300">{formatDate(profile.created_at)}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
        </div>
      </div>
    </InstructorLayout>
  );
};

export default InstructorProfilePage;
